// ===========================================
// Custom email — "does the form differ from the saved record?"
// ===========================================

import { richHtmlEqual } from '../../utils/richHtmlCompare';

/**
 * The editor's form state, in the shape the page holds it.
 *
 * `minAge`/`maxAge` are `number | ''` because they are bound to text inputs —
 * an emptied field is `''`, which must compare equal to a saved `null`.
 */
export interface CustomEmailFormState {
  title: string;
  subject: string;
  preheader: string;
  content: string;
  sendToAll: boolean;
  targetTeachers: boolean;
  targetAgeGroups: string[];
  targetProgramLevels: string[];
  targetInstruments: string[];
  targetClasses: string[];
  targetFamilies: string[];
  minAge: number | '';
  maxAge: number | '';
}

/** The subset of the saved record this comparison reads. */
export interface SavedCustomEmail {
  title: string;
  subject: string;
  preheader?: string | null;
  content: string;
  sendToAll: boolean;
  targetTeachers: boolean;
  targetAgeGroups?: string[];
  targetProgramLevels?: string[];
  targetInstruments?: string[];
  targetClasses?: string[];
  targetFamilies?: string[];
  minAge?: number | null;
  maxAge?: number | null;
}

/**
 * Order-insensitive set comparison.
 *
 * The targeting pickers are Autocompletes, which can hand back the same
 * selection in a different order. Treating a reorder as an edit would block
 * sending for no reason.
 */
function sameSet(a: string[], b: string[] | undefined): boolean {
  const bb = b ?? [];
  return a.length === bb.length && a.every((v) => bb.includes(v));
}

/**
 * Does the form differ from the SAVED record?
 *
 * ⚠️ THIS GUARD IS SAFETY-CRITICAL. Send and Schedule pass only the campaign id —
 * the server mails whatever was last SAVED. So an admin who edits the content
 * and clicks Send Now without saving would mail the OLD version, silently, to
 * the whole audience. It cannot be unsent, and nothing anywhere would tell them
 * it happened. A false "clean" is therefore far worse than a false "dirty".
 *
 * Derived by comparing against the saved record rather than tracking a flag in
 * every setter: a dirty-flag approach silently stops working the moment someone
 * adds a field and forgets to set it, which is exactly the kind of omission this
 * guard exists to catch. Comparison is self-maintaining.
 *
 * ⚠️ Content is compared via `richHtmlEqual`, NOT `!==`. Quill emits unclosed
 * voids (`<br>`) while the backend's sanitizer stores self-closed ones (`<br />`)
 * and decodes entities — so a raw comparison reports a saved campaign as dirty
 * forever, permanently disabling Send and Schedule. That was a real reported
 * bug. Only serialization noise is normalized away; a real content edit is still
 * dirty.
 *
 * ⚠️ EXTRACTED FROM THE PAGE ON PURPOSE. The guard test used to mirror this
 * predicate by hand and covered only 4 of its fields — including neither
 * `sendToAll` (which decides "one class" vs "the whole school") nor
 * `targetFamilies`. A mirror that drifts reads as "tested" while most of the
 * rule is uncovered. Page and tests must import THIS function, so drift is
 * structurally impossible. Add a field to the form → add it here → both follow.
 */
export function isCustomEmailDirty(
  form: CustomEmailFormState,
  saved: SavedCustomEmail | null | undefined,
  isNew: boolean
): boolean {
  // A new campaign has nothing saved to compare against; Save is the only
  // available action, so it is never "dirty" in the blocking sense.
  if (isNew || !saved) return false;

  const toAge = (v: number | '') => (v === '' ? null : Number(v));

  return (
    form.title !== saved.title ||
    form.subject !== saved.subject ||
    (form.preheader || '') !== (saved.preheader || '') ||
    !richHtmlEqual(form.content, saved.content) ||
    form.sendToAll !== saved.sendToAll ||
    form.targetTeachers !== saved.targetTeachers ||
    !sameSet(form.targetAgeGroups, saved.targetAgeGroups) ||
    !sameSet(form.targetProgramLevels, saved.targetProgramLevels) ||
    !sameSet(form.targetInstruments, saved.targetInstruments) ||
    !sameSet(form.targetClasses, saved.targetClasses) ||
    !sameSet(form.targetFamilies, saved.targetFamilies) ||
    toAge(form.minAge) !== (saved.minAge ?? null) ||
    toAge(form.maxAge) !== (saved.maxAge ?? null)
  );
}
